import React, { useState } from 'react';
import { PlayCircleIcon, InstagramIcon } from 'lucide-react';
type Category = 'All' | 'The Lounge' | 'Dining' | 'Cocktails' | 'Events';
interface GalleryItem {
  image: string;
  caption: string;
  category: Exclude<Category, 'All'>;
  type: 'photo' | 'video';
  tall: boolean;
}
const galleryItems: GalleryItem[] = [{
  image: 'https://images.unsplash.com/photo-1574096079513-a8225ba06814?w=800&q=80',
  caption: 'Friday Night Vibes',
  category: 'The Lounge',
  type: 'photo',
  tall: true
}, {
  image: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800&q=80',
  caption: 'Jollof Rice Perfection',
  category: 'Dining',
  type: 'photo',
  tall: false
}, {
  image: 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=800&q=80',
  caption: 'Signature Cocktails',
  category: 'Cocktails',
  type: 'photo',
  tall: true
}, {
  image: 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=800&q=80',
  caption: 'The Crowd Goes Wild',
  category: 'The Lounge',
  type: 'video',
  tall: false
}, {
  image: 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=800&q=80',
  caption: 'Suya Night Special',
  category: 'Dining',
  type: 'photo',
  tall: false
}, {
  image: 'https://images.unsplash.com/photo-1536935338788-846bb9981813?w=800&q=80',
  caption: 'Hibiscus Margarita',
  category: 'Cocktails',
  type: 'photo',
  tall: true
}, {
  image: 'https://images.unsplash.com/photo-1559339352-11d035aa65de?w=800&q=80',
  caption: 'VIP Section Saturday',
  category: 'Events',
  type: 'video',
  tall: true
}, {
  image: 'https://images.unsplash.com/photo-1528735602780-2552fd46c7af?w=800&q=80',
  caption: 'Plantain Platter',
  category: 'Dining',
  type: 'photo',
  tall: false
}, {
  image: 'https://images.unsplash.com/photo-1516997121675-4c2d1684aa3e?w=600&q=80',
  caption: 'Late Night Energy',
  category: 'The Lounge',
  type: 'video',
  tall: false
}, {
  image: 'https://images.unsplash.com/photo-1470337458703-4151201468ce?w=600&q=80',
  caption: 'Birthday Celebration',
  category: 'Events',
  type: 'photo',
  tall: true
}, {
  image: 'https://images.unsplash.com/photo-1515443961218-a51367888e4b?w=800&q=80',
  caption: 'Grilled Tilapia',
  category: 'Dining',
  type: 'photo',
  tall: false
}, {
  image: 'https://images.unsplash.com/photo-1563514995573-04283fd34e1e?w=600&q=80',
  caption: 'DJ Set — Afrobeats Night',
  category: 'The Lounge',
  type: 'video',
  tall: true
}];
export function Gallery() {
  const [activeFilter, setActiveFilter] = useState<Category>('All');
  const categories: Category[] = ['All', 'The Lounge', 'Dining', 'Cocktails', 'Events'];
  const filteredItems = galleryItems.filter(item => activeFilter === 'All' || item.category === activeFilter);
  return <main className="pt-24 min-h-screen bg-matte" data-id="element-323">
      {/* Header */}
      <div className="text-center py-16 px-6 fade-section" data-id="element-324">
        <h1 className="font-serif text-[50px] md:text-[62px] text-white mb-4" data-id="element-325">
          THE GALLERY
        </h1>
        <div className="w-16 h-px bg-gold mx-auto mb-6" data-id="element-326" />
        <p className="font-sans text-lg text-white/60 max-w-2xl mx-auto" data-id="element-327">
          Nights at Tempoo — where every evening tells a story
        </p>
      </div>

      {/* Filter Tabs */}
      <div className="max-w-7xl mx-auto px-6 mb-12 fade-section" data-id="element-328">
        <div className="flex flex-wrap justify-center gap-6 md:gap-12 border-b border-white/10 pb-4" data-id="element-329">
          {categories.map(category => <button key={category} onClick={() => setActiveFilter(category)} className={`relative font-sans text-sm md:text-base uppercase tracking-widest pb-4 transition-colors duration-300 ${activeFilter === category ? 'text-white' : 'text-white/40 hover:text-white/70'}`} data-id="element-330">
              {category}
              {activeFilter === category && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gold" data-id="element-331" />}
            </button>)}
        </div>
      </div>

      {/* Masonry Grid */}
      <div className="max-w-7xl mx-auto px-6 pb-24 fade-section" data-id="element-332">
        <div className="columns-1 md:columns-2 lg:columns-3 gap-4 space-y-4" data-id="element-333">
          {filteredItems.map((item, index) => <div key={index} className={`relative break-inside-avoid overflow-hidden group cursor-pointer w-full ${item.tall ? 'h-[450px] md:h-[500px]' : 'h-[300px] md:h-[350px]'}`} data-id="element-334">
              <img src={item.image} alt={item.caption} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-335" />

              {/* Video Badge (always visible if video) */}
              {item.type === 'video' && <div className="absolute top-4 right-4 z-10 bg-black/50 rounded-full p-2 backdrop-blur-sm" data-id="element-336">
                  <PlayCircleIcon className="w-6 h-6 text-white" data-id="element-337" />
                </div>}

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6 md:p-8" data-id="element-338">
                <span className="font-sans text-xs tracking-widest text-gold uppercase mb-2" data-id="element-339">
                  {item.category}
                </span>
                <h3 className="font-serif text-2xl text-white flex items-center gap-3" data-id="element-340">
                  {item.caption}
                  {item.type === 'video' && <PlayCircleIcon className="w-6 h-6 text-gold" data-id="element-341" />}
                </h3>
              </div>
            </div>)}
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="text-center pb-28 fade-section" data-id="element-342">
        <a href="https://www.instagram.com/tempoo__restaurant/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 border border-gold text-gold px-8 py-4 font-sans uppercase tracking-widest text-base font-medium hover:bg-gold/10 transition-all duration-300" data-id="element-343">
          <InstagramIcon className="w-5 h-5" data-id="element-344" />
          See More on Instagram
        </a>
      </div>
    </main>;
}